import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardMedia,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
  Skeleton,
  Fab,
  useScrollTrigger,
  Zoom,
  Chip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ArrowForward } from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate } from 'react-router-dom';

function Home() {
  const [newProducts, setNewProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // WhatsApp butonu için scroll trigger
  const scrollTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const q = query(
          collection(db, 'products'),
          orderBy('createdAt', 'desc'),
          limit(8)
        );
        const snapshot = await getDocs(q);
        const productsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setNewProducts(productsData);
      } catch (error) {
        console.error('Ürünler yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const ProductCard = ({ product }) => (
    <Card
      component={RouterLink}
      to={`/product/${product.id}`}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        textDecoration: 'none',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          '& .product-image': {
            transform: 'scale(1.05)'
          }
        }
      }}
    >
      {/* Ürün Görseli */}
      <Box sx={{ 
        position: 'relative',
        paddingTop: '133%',
        bgcolor: '#f5f5f5',
        overflow: 'hidden'
      }}>
        <CardMedia
          component="img"
          image={product.images?.[0]}
          alt={product.title}
          className="product-image"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease'
          }}
        />
        {/* İndirim Etiketi */}
        {product.discount && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              bgcolor: 'error.main',
              color: 'white',
              px: 2,
              py: 0.5,
              borderRadius: '20px',
              fontSize: '0.875rem',
              fontWeight: 600,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 1
            }}
          >
            %{product.discount}
          </Box>
        )}
      </Box>

      {/* Ürün Detayları */}
      <CardContent sx={{ p: 2, flexGrow: 1 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontSize: '1.1rem',
            fontWeight: 500,
            mb: 1,
            color: 'text.primary'
          }}
        >
          {product.title}
        </Typography>

        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            mb: 1
          }}
        >
          {product.price?.toLocaleString('tr-TR')} TL
        </Typography>

        {/* Bedenler */}
        <Box sx={{ 
          display: 'flex', 
          gap: 0.5, 
          flexWrap: 'wrap',
          mt: 1 
        }}>
          {product.sizes?.map((size) => (
            <Chip
              key={size}
              label={size}
              size="small"
              variant="outlined"
              sx={{
                borderRadius: '4px',
                height: '24px',
                fontSize: '0.75rem'
              }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ 
      minHeight: '100vh',
      mt: { xs: '56px', sm: '64px' }
    }}>
      {/* Ana Banner */}
      <Box sx={{ 
        position: 'relative',
        height: { xs: '50vh', sm: '70vh' },
        overflow: 'hidden'
      }}>
        <Box
          component="img"
          src="/images/banner.jpg" // Kadın modasını yansıtan şık bir banner
          alt="Banner"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'brightness(0.85)'
          }}
        />
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          width: '100%',
          px: 2
        }}>
          <Typography 
            variant={isMobile ? 'h4' : 'h2'} 
            component="h1" 
            sx={{ 
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              mb: 2,
              fontWeight: 'bold'
            }}
          >
            Yeni Sezon Koleksiyonu
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/categories')}
            sx={{
              backgroundColor: 'white',
              color: 'black',
              '&:hover': {
                backgroundColor: 'grey.100'
              }
            }}
          >
            Koleksiyonu Keşfet
          </Button>
        </Box>
      </Box>

      {/* Ana Kategori Kartları */}
      <Box sx={{ 
        bgcolor: '#f8f8f8', 
        py: 6,
        mt: { xs: 4, sm: 6, md: 8 }
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card 
                component={RouterLink} 
                to="/category/elbiseler"
                sx={{ 
                  position: 'relative',
                  height: { xs: 300, md: 400 },
                  textDecoration: 'none',
                  overflow: 'hidden',
                  '&:hover img': {
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <CardMedia
                  component="img"
                  image="/images/dresses.jpg"
                  alt="Elbiseler"
                  sx={{
                    height: '100%',
                    transition: 'transform 0.6s ease'
                  }}
                />
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 3,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))'
                }}>
                  <Typography variant="h5" sx={{ color: 'white' }}>
                    Elbiseler
                  </Typography>
                </Box>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card 
                component={RouterLink} 
                to="/category/dis-giyim"
                sx={{ 
                  position: 'relative',
                  height: { xs: 300, md: 400 },
                  textDecoration: 'none',
                  overflow: 'hidden',
                  '&:hover img': {
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <CardMedia
                  component="img"
                  image="/images/dis-giyim.jpg"
                  alt="Dış Giyim"
                  sx={{
                    height: '100%',
                    transition: 'transform 0.6s ease'
                  }}
                />
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: 3,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))'
                }}>
                  <Typography variant="h5" sx={{ color: 'white' }}>
                    Dış Giyim
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Yeni Ürünler */}
      <Box sx={{ bgcolor: 'grey.50', py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4
          }}>
            <Typography 
              variant="h4" 
              component="h2"
              sx={{
                fontWeight: 'bold',
                position: 'relative',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 60,
                  height: 2,
                  bgcolor: 'primary.main'
                }
              }}
            >
              Yeni Gelenler
            </Typography>
            <Button
              component={RouterLink}
              to="/products"
              endIcon={<ArrowForward />}
              sx={{ 
                color: 'text.primary',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: 'transparent',
                  color: 'primary.main'
                }
              }}
            >
              Tümünü Gör
            </Button>
          </Box>

          <Grid container spacing={2}>
            {(loading ? Array(4).fill(null) : newProducts.slice(0, 4)).map((product, index) => (
              <Grid item key={product?.id || index} xs={6} sm={6} md={3}>
                {loading ? (
                  <Skeleton variant="rectangular" height={400} />
                ) : (
                  <ProductCard product={product} />
                )}
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Trend Kategoriler - Yeni Tasarım */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h4" 
            component="h2" 
            sx={{ 
              mb: 6, 
              textAlign: 'center',
              fontWeight: 'bold',
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 60,
                height: 3,
                bgcolor: 'primary.main'
              }
            }}
          >
            Trend Kategoriler
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                title: 'Elbiseler',
                image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8',
                path: '/category/elbiseler'
              },
              {
                title: 'Üst Giyim',
                image: 'https://images.unsplash.com/photo-1551799517-eb8f03cb5e6a',
                path: '/category/Üst%20giyim'
              },
              {
                title: 'Alt Giyim',
                image: 'https://images.unsplash.com/photo-1584370848010-d7fe6bc767ec',
                path: '/category/alt-giyim'
              },
              {
                title: 'Dış Giyim',
                image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea',
                path: '/category/dis-giyim'
              }
            ].map((category) => (
              <Grid item xs={12} sm={6} md={3} key={category.title}>
                <Card
                  component={RouterLink}
                  to={category.path}
                  sx={{
                    position: 'relative',
                    height: 300,
                    overflow: 'hidden',
                    textDecoration: 'none',
                    '&:hover': {
                      '& img': {
                        transform: 'scale(1.1)',
                      },
                      '& .MuiBox-root': {
                        bgcolor: 'rgba(0,0,0,0.6)'
                      }
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={category.image}
                    alt={category.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: 'rgba(0,0,0,0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        px: 2,
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                      }}
                    >
                      {category.title}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* WhatsApp Balonu */}
      <Fab
        component="a"
        href="https://wa.me/905316949744"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          position: 'fixed',
          bottom: { xs: '80px', sm: 24 },
          right: { xs: '16px', sm: 24 },
          bgcolor: '#25D366',
          display: { xs: 'flex', sm: 'flex' },
          '&:hover': {
            bgcolor: '#128C7E'
          },
          zIndex: 9999,
          width: { xs: 50, sm: 56 },
          height: { xs: 50, sm: 56 },
          boxShadow: 3,
        }}
      >
        <WhatsAppIcon sx={{ 
          color: 'white',
          fontSize: { xs: '1.8rem', sm: '2rem' }
        }} />
      </Fab>
    </Box>
  );
}

export default Home; 